/* MORLYF custom interactions
 * Scroll reveal, cursor spotlight, magnetic buttons.
 * Disabled on touch / reduced-motion devices where appropriate.
 */
(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = window.matchMedia('(hover: none)').matches;

  /* -- 1. Reveal on scroll ---------------------------------------------- */
  function initReveal() {
    if (prefersReducedMotion) return;

    const candidates = document.querySelectorAll(
      '.shopify-section > .section, .shopify-section > [class*="section-"], main .shopify-section'
    );

    candidates.forEach((el) => {
      // Skip header/footer sections and the already-visible hero
      if (el.closest('#header-group') || el.closest('footer')) return;
      el.classList.add('morlyf-reveal');
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -80px 0px' }
    );

    document.querySelectorAll('.morlyf-reveal').forEach((el) => observer.observe(el));

    // Reveal anything already in view on load (first screen)
    requestAnimationFrame(() => {
      document.querySelectorAll('.morlyf-reveal').forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          el.classList.add('is-visible');
          observer.unobserve(el);
        }
      });
    });
  }

  /* -- 2. Cursor spotlight ---------------------------------------------- */
  function initSpotlight() {
    if (isTouch || prefersReducedMotion) return;

    const el = document.createElement('div');
    el.className = 'morlyf-spotlight';
    document.body.appendChild(el);

    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let currentX = targetX;
    let currentY = targetY;
    let animating = false;

    function tick() {
      currentX += (targetX - currentX) * 0.16;
      currentY += (targetY - currentY) * 0.16;
      el.style.transform = 'translate3d(' + currentX + 'px,' + currentY + 'px,0)';
      if (Math.abs(targetX - currentX) > 0.5 || Math.abs(targetY - currentY) > 0.5) {
        requestAnimationFrame(tick);
      } else {
        animating = false;
      }
    }

    document.addEventListener('pointermove', (e) => {
      targetX = e.clientX;
      targetY = e.clientY;
      el.classList.add('is-active');
      if (!animating) {
        animating = true;
        requestAnimationFrame(tick);
      }
    });

    document.addEventListener('pointerleave', () => el.classList.remove('is-active'));
    window.addEventListener('blur', () => el.classList.remove('is-active'));
  }

  /* -- 3. Magnetic buttons ---------------------------------------------- */
  function initMagnetic() {
    if (isTouch || prefersReducedMotion) return;

    const selectors = [
      '.button',
      'a.button',
      'button.button',
      '.waitlist-spine-container .button',
      '.shopify-payment-button__button'
    ];
    const buttons = document.querySelectorAll(selectors.join(','));

    buttons.forEach((btn) => {
      if (btn.dataset.morlyfMagnetic) return;
      btn.dataset.morlyfMagnetic = '1';
      btn.classList.add('morlyf-magnetic');

      const strength = 0.28; // max pull as fraction of distance
      const radius = 90;     // px from centre at which pull is maxed

      btn.addEventListener('pointermove', (e) => {
        const rect = btn.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.min(Math.sqrt(dx * dx + dy * dy), radius);
        const pull = (dist / radius) * strength;
        btn.style.transform = 'translate3d(' + (dx * pull) + 'px,' + (dy * pull) + 'px,0)';
      });

      btn.addEventListener('pointerleave', () => {
        btn.style.transform = '';
      });
    });
  }

  /* -- 4. Title split ----------------------------------------------------
   * Splits any product title containing " (...)" into a main line plus a
   * smaller subtitle. Targets the H1 on product detail pages AND every
   * product-title link on cards (homepage featured grid, collection grids,
   * search results, recommendations).
   *
   * Idempotent via data-morlyf-split, so it won't double-process titles
   * already handled by the standalone sections/morlyf-title-split.liquid
   * section that was the original implementation on product detail pages.
   */
  function initTitleSplit() {
    const targets = document.querySelectorAll(
      '.shopify-section h1, [ref="productTitleLink"]'
    );

    targets.forEach((node) => {
      if (node.dataset.morlyfSplit) return;

      // For product card links, the actual text node is inside a child <p>.
      // For h1 we operate on the h1 itself.
      const host = node.tagName === 'A'
        ? (node.querySelector('p') || node)
        : node;

      const fullText = (host.textContent || '').trim();
      const idx = fullText.indexOf(' (');
      if (idx <= 0) return;

      const main = fullText.slice(0, idx);
      const sub = fullText.slice(idx + 2).replace(/\)\s*$/, '');

      host.innerHTML =
        main +
        '<span class="morlyf-title-sub" style="display:block;font-size:0.7em;font-weight:400;letter-spacing:0.02em;margin-top:0.4em;opacity:0.92;">' +
        sub +
        '</span>';

      node.dataset.morlyfSplit = '1';
    });
  }

  /* -- 5. Sticky pill horizontal tracking --------------------------------
   * The Horizon sticky pill is fixed at viewport centre. On product pages
   * with desktop_media_position: left, the pill ends up between columns,
   * not under the image. This routine measures the actual gallery element
   * each frame and pins the pill's centre to match. Mobile (≤749px) keeps
   * the default centred-on-viewport positioning (single-column layout).
   */
  function initPillTracking() {
    const pill = document.querySelector('.sticky-add-to-cart__bar');
    if (!pill) return;

    const gallery =
      document.querySelector('.product-media-gallery') ||
      document.querySelector('product-media-gallery') ||
      document.querySelector('[id$="-media-gallery"]') ||
      document.querySelector('.product-information [class*="media-gallery"]') ||
      document.querySelector('.product-information__media');
    if (!gallery) return;

    let scheduled = false;
    const mobileMQ = window.matchMedia('(max-width: 749px)');
    function update() {
      scheduled = false;
      if (mobileMQ.matches) {
        pill.style.removeProperty('left');
        return;
      }
      const rect = gallery.getBoundingClientRect();
      if (rect.width === 0) return;
      const centerX = rect.left + rect.width / 2;
      pill.style.setProperty('left', centerX + 'px', 'important');
    }
    function scheduleScroll() {
      // Mobile uses default full-width sticky bar - no per-scroll positioning
      // needed. Skip rAF entirely to keep scroll buttery smooth on phones.
      if (mobileMQ.matches) return;
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    }
    function scheduleResize() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    }
    update();
    window.addEventListener('resize', scheduleResize, { passive: true });
    window.addEventListener('scroll', scheduleScroll, { passive: true });
  }

  /* -- 6. Preorder modal at checkout --------------------------------------
   * If the user clicks the checkout button on the cart page or drawer and
   * any item has product.template_suffix == 'preorder' (rendered via Liquid
   * to data-cart-has-preorder="true" on the button), pause submission and
   * show a confirmation modal warning about the 2-4 week wait. On confirm
   * we submit the cart form. On cancel we close the modal.
   *
   * Limitation: only intercepts the standard #checkout submit button. The
   * accelerated checkout buttons (Shop Pay, Apple Pay, Google Pay, PayPal)
   * are iframed and bypass this — the inline preorder banner in cart should
   * still surface the warning before they tap those.
   */
  let preorderModalEl = null;
  function ensurePreorderModalNode() {
    if (preorderModalEl) return preorderModalEl;
    const wrap = document.createElement('div');
    wrap.className = 'morlyf-preorder-modal';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'morlyf-preorder-title');
    wrap.innerHTML = '<div class="morlyf-preorder-modal__backdrop" data-close="1"></div>'
      + '<div class="morlyf-preorder-modal__panel" role="document">'
      + '<h2 id="morlyf-preorder-title" class="morlyf-preorder-modal__title">Pre-order in your cart</h2>'
      + '<p>Your pre-order can take 2-4 weeks to arrive.</p>'
      + '<p>For any queries, please email <a href="mailto:hello@morlyf.com.au">hello@morlyf.com.au</a></p>'
      + '<div class="morlyf-preorder-modal__buttons">'
      + '<button type="button" class="morlyf-preorder-modal__cancel" data-close="1">Keep shopping</button>'
      + '<button type="button" class="morlyf-preorder-modal__continue">Continue to checkout</button>'
      + '</div></div>';
    document.body.appendChild(wrap);
    preorderModalEl = wrap;
    wrap.addEventListener('click', function(e) {
      if (e.target.dataset && e.target.dataset.close === '1') closePreorderModal();
    });
    return wrap;
  }
  function openPreorderModal(onConfirm) {
    const wrap = ensurePreorderModalNode();
    const cont = wrap.querySelector('.morlyf-preorder-modal__continue');
    const newCont = cont.cloneNode(true);
    cont.replaceWith(newCont);
    newCont.addEventListener('click', function() {
      closePreorderModal();
      if (typeof onConfirm === 'function') onConfirm();
    });
    wrap.dataset.open = 'true';
    document.body.style.overflow = 'hidden';
  }
  function closePreorderModal() {
    if (!preorderModalEl) return;
    preorderModalEl.dataset.open = 'false';
    document.body.style.overflow = '';
  }

  function initPreorderModal() {
    // Event delegation: catches the button even after cart morphs re-render it.
    if (window.__morlyfPreorderBound) return;
    window.__morlyfPreorderBound = true;
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('button[name="checkout"]');
      if (!btn) return;
      if (btn.dataset.cartHasPreorder !== 'true') return;
      e.preventDefault();
      e.stopPropagation();
      openPreorderModal(function() {
        const form = btn.form || document.getElementById('cart-form') || document.querySelector('form[action*="/cart"]');
        if (form) {
          // Replicate the checkout button's submit semantics.
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'checkout';
          hidden.value = '';
          form.appendChild(hidden);
          form.submit();
        }
      });
    }, true); // capture phase to beat native submit handlers
  }

  /* -- 7. iOS Safari visualViewport pin for the sticky add-to-cart pill ---
   * On iOS Safari the URL bar collapses/expands as the user scrolls, which
   * resizes the LAYOUT viewport. `position: fixed; bottom: 0` references
   * the layout viewport bottom and visibly JIGGLES vertically during the
   * URL bar animation - the pill appears to bounce up and down with scroll
   * momentum. The visualViewport API exposes the actual visible area and
   * fires `resize` events during the URL bar transition. We compute the
   * offset between layout-viewport-bottom and visual-viewport-bottom and
   * push the pill UP by that delta via a CSS variable so it stays pinned
   * to the visible bottom edge through the entire URL bar animation. */
  function initVisualViewportPillPin() {
    if (!window.visualViewport) return;
    const root = document.documentElement;
    let scheduled = false;
    function update() {
      scheduled = false;
      const vv = window.visualViewport;
      // Pixels between the bottom of the layout viewport and the bottom
      // of the visual viewport. > 0 when the URL bar is visible.
      const delta = Math.max(0, window.innerHeight - (vv.offsetTop + vv.height));
      root.style.setProperty('--vv-bottom-offset', delta + 'px');
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    }
    update();
    window.visualViewport.addEventListener('resize', schedule, { passive: true });
    window.visualViewport.addEventListener('scroll', schedule, { passive: true });
  }

  function boot() {
    initReveal();
    initSpotlight();
    initMagnetic();
    initTitleSplit();
    initPillTracking();
    initVisualViewportPillPin();
    initPreorderModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-bind on Shopify section reloads in the theme editor + after async card swaps
  document.addEventListener('shopify:section:load', () => {
    initMagnetic();
    initTitleSplit();
    initPillTracking();
  });
})();


/* Age gate fix: "SB: Age Verification" app has its Accept/Decline button TEXT
   swapped (the enter button .sb_av_btn_yes was labelled "Decline", the stay
   button .sb_av_btn_no labelled "Accept"). Force labels to match the real
   actions and keep Accept on the left. Safe to remove once the app's button
   text settings are corrected. */
(function morlyfAgeGateFix(){
  function fix(){
    var yes = document.querySelector('.sb_av_btn_yes');
    var no  = document.querySelector('.sb_av_btn_no');
    if(!yes || !no) return;
    var ys = yes.querySelector('span') || yes;
    var ns = no.querySelector('span') || no;
    if(ys.textContent.trim() !== 'Accept') ys.textContent = 'Accept';
    if(ns.textContent.trim() !== 'Decline') ns.textContent = 'Decline';
    if(yes.parentNode === no.parentNode &&
       (no.compareDocumentPosition(yes) & Node.DOCUMENT_POSITION_FOLLOWING)){
      no.parentNode.insertBefore(yes, no);
    }
  }
  if(document.readyState !== 'loading') fix();
  else document.addEventListener('DOMContentLoaded', fix);
  try { new MutationObserver(fix).observe(document.documentElement, {childList:true, subtree:true}); } catch(e){}
})();

/* Age gate: replace the app's white-backed logo with the transparent SITE logo (same as header). */
(function morlyfGateLogo(){
  var LOGO = 'https://morlyf.com.au/cdn/shop/files/FullLogo_Transparent_NoBuffer_10.png?width=260';
  function fix(){
    var imgs = document.querySelectorAll('.sb_av_cover_popup_logo_center img');
    for (var i=0;i<imgs.length;i++){ if(imgs[i].getAttribute('src') !== LOGO){ imgs[i].setAttribute('src', LOGO); } }
  }
  if(document.readyState!=='loading') fix(); else document.addEventListener('DOMContentLoaded', fix);
  try { new MutationObserver(fix).observe(document.documentElement,{childList:true,subtree:true}); } catch(e){}
})();
